/**
 * SyncService — platform-agnostic sync engine with dependency injection.
 *
 * All platform-specific dependencies (storage, HTTP, events) are injected
 * via the constructor, making this class testable with mock adapters.
 *
 * This class contains the core sync logic extracted from lib/sync.ts:
 * - pushToServer() — flush the sync queue via POST /sync/push
 * - pullFromServer() — pull changes since last version via POST /sync/pull
 * - fullSync() — push then pull with mutex protection
 * - resolveConflict() — resolve note conflicts (server or local wins)
 */

import { deduplicateSyncOps, normalizeTagIds, stripNoteTextFields, threeWayMerge } from "@caliu/utils";
import type {
  Note,
  SyncEventBus,
  SyncHttpClient,
  SyncOperation,
  SyncPullResponse,
  SyncPushResponse,
  SyncPushResult,
  SyncResult,
  SyncState,
  SyncStatus,
  SyncStorage,
} from "../../types";
import { logSyncConflict, Sentry } from "../sentry";
import { ContentSync, type ContentSyncStore, type SyncDeviceSource } from "./contentSync";

// ── Constants ─────────────────────────────────────────────────────────────

const MAX_RETRIES = 3;
const MAX_PUSH_BATCH = 100;

// ── ApiError-like interface for type-safe error handling ──────────────────

interface ApiErrorLike {
  status: number;
  message: string;
}

function isApiError(err: unknown): err is ApiErrorLike {
  return (
    typeof err === "object" &&
    err !== null &&
    "status" in err &&
    typeof (err as ApiErrorLike).status === "number"
  );
}

/**
 * Rewrites tag-id references in a push op using client→server reconciliations
 * discovered while applying earlier batches. Returns the op untouched when
 * nothing matches. Covers a child tag's `parentId`/`parent_id`, a note_tag's
 * `tagId`/`tag_id`, a tag op's own `entityId`, and a note_tag's `entityId`
 * (shaped `noteId:tagId`).
 */
function applyTagIdRemap<
  T extends { entity: string; entityId: string; data: Record<string, unknown> },
>(op: T, remap: Map<string, string>): T {
  if (remap.size === 0) return op;

  let entityId = op.entityId;
  if (op.entity === "tag") {
    entityId = remap.get(entityId) ?? entityId;
  } else if (op.entity === "note_tag") {
    const sep = entityId.lastIndexOf(":");
    if (sep >= 0) {
      const mapped = remap.get(entityId.slice(sep + 1));
      if (mapped) entityId = `${entityId.slice(0, sep)}:${mapped}`;
    }
  }

  let data = op.data;
  for (const key of ["parentId", "parent_id", "tagId", "tag_id"]) {
    const value = data[key];
    if (typeof value === "string" && remap.has(value)) {
      if (data === op.data) data = { ...op.data };
      data[key] = remap.get(value)!;
    }
  }

  if (entityId === op.entityId && data === op.data) return op;
  return { ...op, entityId, data };
}

// ── SyncService ──────────────────────────────────────────────────────────

export interface SyncServiceDeps {
  storage: SyncStorage;
  http: SyncHttpClient;
  events: SyncEventBus;
  /** Check if the client is online. Defaults to () => navigator.onLine. */
  isOnline?: () => boolean;
  /** Get the current authenticated user's ID. Used to set user_id on pulled entities. */
  getUserId?: () => string;
  /**
   * Conflict-free content sync (PLAN.md task 1.9). Absent: legacy only, as
   * before. Present: the mode follows the pull's `collabFeatures.syncV2` (D8).
   */
  content?: {
    store: ContentModeStore;
    device: SyncDeviceSource;
    /** Caller rule 2: flush the open editor's typing before remote ops land. */
    beforeRemote?: (noteId: string) => void;
  };
}

/** The storage side of the v2 mode: the engine plus the D8 switches. */
export interface ContentModeStore extends ContentSyncStore {
  getSyncMode(): "legacy" | "v2";
  /** Remembers `collabFeatures.live` (the open note's socket reads it). */
  setLiveFeature?(on: boolean): void;
  /** Remembers `collabFeatures.sharing` (lists and the share sheet read it). */
  setSharingFeature?(on: boolean): void;
  /** `maxRetries`: an op past it is never sent again (the upgrade does not wait for it). */
  switchToV2(maxRetries: number): void;
  switchToLegacy(): void;
  enterEngineAfterCreate(
    noteId: string,
    reply: { rev?: number; text?: string },
    pushed: { content: string; updatedAt: string },
  ): void;
}

/** How many times an auto-merge is redone because the row changed under it. */
const MAX_AUTO_MERGE_ROUNDS = 3;

/** A storage whose auto-merge write checks the text it was computed from. */
interface GuardedAutoMergeStorage {
  applyAutoMergedNoteIfUnchanged(
    noteId: string,
    mergedContent: string,
    expectedUpdatedAt: string,
    mergedFrom: string,
  ): Promise<boolean>;
}

/** A storage that can do "Keep Server" in one transaction (web does). */
interface KeepServerStorage {
  keepServerNote(note: Note & { syncStatus: SyncStatus }, opIds: number[]): Promise<void>;
}

export class SyncService {
  private _storage: SyncStorage;
  private _http: SyncHttpClient;
  private _events: SyncEventBus;
  private _isOnline: () => boolean;
  private _getUserId: () => string;

  private _content: SyncServiceDeps["content"];

  private _syncState: SyncState = "idle";
  private _syncInProgress: Promise<SyncResult> | null = null;

  constructor(deps: SyncServiceDeps) {
    this._storage = deps.storage;
    this._http = deps.http;
    this._events = deps.events;
    this._isOnline = deps.isOnline ?? (() => navigator.onLine);
    this._getUserId = deps.getUserId ?? (() => "");
    this._content = deps.content;
  }

  // ── Content sync mode (PLAN.md D8) ───────────────────────────────────

  /** v2: note text goes through `/sync/content`; legacy: today's behaviour. */
  private _isV2(): boolean {
    return this._content?.store.getSyncMode() === "v2";
  }

  /**
   * Follows the pull's features. Going back to legacy runs before the pull's
   * changes are applied (they then apply as legacy). Going to v2 runs after:
   * legacy moves a note's base only when a pull brings its change back, so
   * the pull that echoes this device's landed pushes must land first, or the
   * upgrade would put those pushed edits in the buffer and send them twice.
   */
  private _applyCollabFeatures(features: SyncPullResponse["collabFeatures"], phase: "before" | "after"): void {
    const store = this._content?.store;
    if (!store) return;
    const wantV2 = features?.syncV2 === true;
    const mode = store.getSyncMode();
    if (phase === "before") {
      store.setLiveFeature?.(wantV2 && features?.live === true);
      store.setSharingFeature?.(wantV2 && features?.sharing === true);
      if (!wantV2 && mode === "v2") store.switchToLegacy();
    } else if (wantV2 && mode === "legacy") {
      store.switchToV2(MAX_RETRIES);
    }
  }

  private _contentRun: Promise<void> = Promise.resolve();

  /**
   * A content pass after the metadata push and after every pull (the pull
   * says which notes moved). Passes run one after the other, never at once.
   */
  private _syncContent(): Promise<void> {
    const run = this._contentRun.catch(() => undefined).then(() => this._syncContentOnce());
    this._contentRun = run;
    return run;
  }

  /** One content pass; a `disabled` answer switches this device to legacy. */
  /**
   * A content pass that never fails the push or pull around it: an
   * unexpected failure is reported, and the next pass retries.
   */
  private async _syncContentSafely(): Promise<void> {
    try {
      await this._syncContent();
    } catch (err) {
      if (!(isApiError(err) && err.status === 401) && this._isOnline()) Sentry.captureException(err);
    }
  }

  private async _syncContentOnce(): Promise<void> {
    const content = this._content;
    if (!content || !this._isV2()) return;
    const driver = new ContentSync({
      http: this._http,
      store: content.store,
      device: content.device,
      beforeRemote: content.beforeRemote,
    });
    const { disabled } = await driver.run();
    if (disabled) content.store.switchToLegacy();
  }

  // ── State management ─────────────────────────────────────────────────

  getSyncState(): SyncState {
    return this._syncState;
  }

  private broadcastSyncState(state: SyncState): void {
    this._syncState = state;
    this._events.emit("sync-state", state);
  }

  // ── pushToServer (v2 — batch push) ───────────────────────────────────

  async pushToServer(): Promise<SyncResult> {
    const result = await this._pushOps();
    // v2 (PLAN.md task 1.9): the metadata push, then the content.
    if (this._isOnline()) await this._syncContentSafely();
    return result;
  }

  private async _pushOps(): Promise<SyncResult> {
    if (!this._isOnline()) return { synced: 0, failed: 0, skipped: 0 };

    const ops = await this._storage.getAllSyncOps();
    if (ops.length === 0) return { synced: 0, failed: 0, skipped: 0 };

    // Deduplicate using shared pure function
    const { latest: latestByKey } = deduplicateSyncOps(ops);

    // Superseded ops MUST stay in the queue until the server confirms the op
    // that absorbed them. Dedup merges offline `update` content into a pending
    // `create` IN MEMORY only — the queue row still carries the create's
    // original (empty) content. Deleting the superseded updates before the
    // push lands means a failed push (flaky network, tab closed mid-request)
    // leaves the queue with just the bare create; the next successful sync
    // then creates an EMPTY server note, whose pull echo overwrites the local
    // content — the "note written offline disappears when I come back online"
    // bug. So: group superseded ids by their winning op's key and delete each
    // group only when that op is applied / conflicted (via _deleteOpGroup).
    const latestIds = new Set(
      [...latestByKey.values()].map((op) => op.id).filter((id) => id !== undefined),
    );
    const supersededIdsByKey = new Map<string, number[]>();
    for (const op of ops) {
      if (op.id === undefined || latestIds.has(op.id)) continue;
      const key = `${op.entity}:${op.entityId}`;
      const group = supersededIdsByKey.get(key) ?? [];
      group.push(op.id);
      supersededIdsByKey.set(key, group);
    }

    const result: SyncResult = {
      synced: 0,
      failed: 0,
      skipped: 0,
    };

    // The ops of a note in conflict are HELD: not sent, not retried, not
    // deleted. A write made while the conflict is open (a keystroke, a pin)
    // keeps the note in `conflict` and queues its op as usual, so nothing is
    // lost on this device; pushing it would overwrite the server's version
    // without the user ever having been asked. resolveConflict() decides
    // their fate: "server" drops them, "local" releases them.
    const heldNoteIds = await this._conflictNoteIds([...latestByKey.values()]);

    // The note's tag links are held with it: they follow its text, and the
    // server would otherwise get links the text it holds does not justify
    // (and, on an unlink, could archive a tag its text still has). `tag` ops
    // are not held: a tag is shared with other notes.
    const isHeld = (op: SyncOperation) =>
      (op.entity === "note" && heldNoteIds.has(op.entityId)) ||
      (op.entity === "note_tag" && heldNoteIds.has(op.entityId.split(":")[0]));
    const winners = [...latestByKey.values()].filter((op) => !isHeld(op));

    // Sort ops so parent tag creates are processed before children
    const sortedOps = winners.sort((a, b) => {
      const aIsChildTag =
        a.entity === "tag" &&
        a.type === "create" &&
        !!(a.data as Record<string, unknown>)?.parentId;
      const bIsChildTag =
        b.entity === "tag" &&
        b.type === "create" &&
        !!(b.data as Record<string, unknown>)?.parentId;
      if (aIsChildTag && !bIsChildTag) return 1;
      if (!aIsChildTag && bIsChildTag) return -1;
      return 0;
    });

    // v2: note text goes through /sync/content, so an update carries its
    // metadata only (D1). One with nothing else is done here, unsent.
    let sendable = sortedOps;
    if (this._isV2()) {
      sendable = [];
      for (const op of sortedOps) {
        const stripped = stripNoteTextFields(op);
        if (stripped) {
          sendable.push(stripped);
          continue;
        }
        await this._deleteOpGroup(op, supersededIdsByKey, result);
        await this._storage.markEntitySynced(op.entity, op.entityId);
        result.synced++;
      }
    }

    // Build push payload from sorted ops (filtering out exhausted retries)
    const eligibleOps = sendable.filter((op) => op.retries < MAX_RETRIES);
    const pushOps = eligibleOps.map((op) => ({
      entity: op.entity as string,
      entityId: op.entityId,
      operation: op.type,
      data: (op.data as Record<string, unknown>) ?? {},
      clientTimestamp: new Date(op.timestamp).toISOString(),
    }));

    if (pushOps.length === 0) {
      result.failed += sendable.length;
      return result;
    }

    // Tracks client→server tag ID reconciliations made while applying earlier
    // batches' results. `pushOps` is built once up front, so a child tag (or
    // note_tag) sitting in a later batch still carries the stale client parentId
    // even after its parent reconciled in an earlier batch. The server's own
    // remap is per-request, so it can't resolve a cross-batch reference. We
    // rewrite the not-yet-sent ops here so the parent resolves on the server.
    const tagIdRemap = new Map<string, string>();

    // Send in sequential batches to respect server limits
    for (let batchStart = 0; batchStart < pushOps.length; batchStart += MAX_PUSH_BATCH) {
      const batchOps = pushOps
        .slice(batchStart, batchStart + MAX_PUSH_BATCH)
        .map((op) => applyTagIdRemap(op, tagIdRemap));
      const batchSourceOps = eligibleOps.slice(batchStart, batchStart + MAX_PUSH_BATCH);

      try {
        const response = await this._http.fetchOrThrow<SyncPushResponse>("/sync/push", {
          method: "POST",
          body: JSON.stringify({ operations: batchOps }),
        });

        for (let i = 0; i < response.results.length; i++) {
          const pushResult = response.results[i];
          const op = batchSourceOps[i];
          if (!op) continue;

          if (pushResult.status === "applied") {
            await this._deleteOpGroup(op, supersededIdsByKey, result);

            if (
              (op.entity === "note" || op.entity === "tag") &&
              (op.type === "update" || op.type === "create")
            ) {
              // markEntitySynced only clears the pending flag; unlike
              // markNoteSynced it does NOT advance base_content. After a clean
              // auto-merge's re-push lands here, the merge ancestor therefore
              // stays at the OLD base until the next pull echoes the merged note
              // back and sets base_content = content. This is the intended
              // "self-heal in one sync cycle" behavior: a conflict during that
              // window merges against a slightly stale base (which over-reports
              // conflicts, never mis-merges). Do NOT "fix" this by advancing the
              // base here.
              await this._storage.markEntitySynced(op.entity, op.entityId);
            }

            // v2: a created note enters the content engine at the reply's
            // {rev, text} (PLAN.md "Creates"). The store checks the mode as it
            // writes (a pull may have switched it since this push started).
            if (op.entity === "note" && op.type === "create") {
              const reply = pushResult as SyncPushResult & { rev?: number; text?: string };
              const data = (op.data as Record<string, unknown> | undefined) ?? {};
              const pushedAt = data.updated_at ?? data.updatedAt;
              this._content?.store.enterEngineAfterCreate(
                op.entityId,
                { rev: reply.rev, text: reply.text },
                {
                  content: typeof data.content === "string" ? data.content : "",
                  updatedAt: typeof pushedAt === "string" ? pushedAt : "",
                },
              );
            }

            // Handle tag ID mapping (server may assign different ID)
            if (pushResult.serverData?.serverId && pushResult.serverData.clientId) {
              const serverId = pushResult.serverData.serverId as string;
              const clientId = pushResult.serverData.clientId as string;
              if (serverId !== clientId) {
                await this._storage.reconcileTagId(clientId, serverId);
                // Rewrite references in later batches that still point at the
                // pre-reconcile client id (e.g. a child tag's parentId).
                tagIdRemap.set(clientId, serverId);
              }
            }

            result.synced++;
          } else if (pushResult.status === "conflict") {
            // Try a git-style three-way auto-merge first. If it merges cleanly
            // (no overlapping edits) we re-push the merged content silently and
            // never surface a conflict. Only truly overlapping edits — or a note
            // with no stored merge base — fall through to manual resolution.
            let autoMerged = false;
            if (op.entity === "note") {
              autoMerged = await this._tryAutoMergeNoteConflict(op, pushResult, ops);
              if (!autoMerged) {
                await this._reportNoteConflict("v2_push", op, pushResult);
                await this._storage.markNoteConflict(op.entityId);
              }
            }
            // Discard the pushed op: on a clean auto-merge its content was
            // merged and re-enqueued; otherwise the user resolves via
            // resolveConflict(). Either way its superseded ops go with it.
            //
            // The re-enqueued merge op is a FRESH op (retries=0), so MAX_RETRIES
            // never bounds the re-merge loop. That is intentional: merges are
            // deterministic and each round consumes the newest server state, so
            // the loop converges as soon as editing pauses. Two devices making
            // non-overlapping edits faster than a sync cycle can ping-pong for a
            // while, but never diverge or lose data.
            await this._deleteOpGroup(op, supersededIdsByKey, result);
            if (autoMerged) result.synced++;
            else result.failed++;
          } else {
            // Error — increment retries
            if (op.id) await this._storage.updateSyncOpRetries(op.id, op.retries + 1);
            result.failed++;
          }
        }

        // Do NOT advance the local sync watermark from the push response.
        // `response.serverVersion` is the *global* `sync_version` counter
        // for this user — it includes every mutation any other device has
        // pushed since our last pull. Storing it here causes the next pull
        // (`sinceVersion = watermark`) to skip those entries, silently
        // dropping cross-device changes (most visibly: `note_tag` rows
        // pushed by another device land in the server sync log but never
        // reach this client). The watermark must only be advanced from
        // `applyPullChanges`, which knows it has actually consumed
        // everything up to that version.
      } catch (err) {
        // 401 — session expired, stop immediately
        if (isApiError(err) && err.status === 401) {
          const remaining = eligibleOps.length - batchStart;
          result.failed += remaining;
          return result;
        }

        // If v2 endpoint not available, fall back to legacy per-op sync
        if (isApiError(err) && (err.status === 404 || err.status === 405)) {
          return this._pushToServerLegacy(sendable, supersededIdsByKey, result);
        }

        // Network or other error — increment retries on remaining ops
        const remainingOps = eligibleOps.slice(batchStart);
        for (const op of remainingOps) {
          if (op.id && op.retries < MAX_RETRIES) {
            await this._storage.updateSyncOpRetries(op.id, op.retries + 1);
          }
        }
        result.failed += remainingOps.length;
        break;
      }
    }

    return result;
  }

  /**
   * Ids of the notes, among those the given ops touch, that are in `conflict`.
   * One COUNT when nothing is in conflict, which is nearly always.
   */
  private async _conflictNoteIds(ops: SyncOperation[]): Promise<Set<string>> {
    const held = new Set<string>();
    if ((await this._storage.getConflictCount()) === 0) return held;
    const seen = new Set<string>();
    for (const op of ops) {
      // A link's entity id is `<noteId>:<tagId>`.
      const noteId =
        op.entity === "note"
          ? op.entityId
          : op.entity === "note_tag"
            ? op.entityId.split(":")[0]
            : null;
      if (noteId === null || seen.has(noteId)) continue;
      seen.add(noteId);
      // Web storage returns LocalNote (with syncStatus); the shared interface
      // narrows to Note.
      const note = (await this._storage.getNote(noteId)) as
        | (Note & { syncStatus?: SyncStatus })
        | undefined;
      if (note?.syncStatus === "conflict") held.add(noteId);
    }
    return held;
  }

  /** Queued ops that belong to a note: its own, and its tag links. */
  private async _queuedOpsForNote(noteId: string): Promise<SyncOperation[]> {
    const ops = await this._storage.getAllSyncOps();
    return ops.filter(
      (op) =>
        (op.entity === "note" && op.entityId === noteId) ||
        (op.entity === "note_tag" && op.entityId.startsWith(`${noteId}:`)),
    );
  }

  /**
   * Deletes a server-confirmed op together with the queued ops it superseded
   * (dedup merged their data into it in memory — see pushToServer). Superseded
   * ops must never leave the queue before this confirmation, or a failed push
   * permanently loses the merged data.
   */
  private async _deleteOpGroup(
    op: SyncOperation,
    supersededIdsByKey: Map<string, number[]>,
    result: SyncResult,
  ): Promise<void> {
    if (op.id) await this._storage.deleteSyncOp(op.id);
    const key = `${op.entity}:${op.entityId}`;
    const group = supersededIdsByKey.get(key);
    if (group) {
      supersededIdsByKey.delete(key);
      for (const id of group) {
        await this._storage.deleteSyncOp(id);
        result.skipped++;
      }
    }
  }

  // ── Legacy push (fallback) ────────────────────────────────────────────

  private async _pushToServerLegacy(
    ops: SyncOperation[],
    supersededIdsByKey: Map<string, number[]>,
    result: SyncResult,
  ): Promise<SyncResult> {
    for (const op of ops) {
      if (op.retries >= MAX_RETRIES) {
        result.failed++;
        continue;
      }

      try {
        if (op.type === "create") {
          const payload = op.data as Record<string, unknown> | undefined;
          await this._http.fetchOrThrow(`/${op.entity}s`, {
            method: "POST",
            body: JSON.stringify(payload),
          });
        } else if (op.type === "update") {
          await this._http.fetchOrThrow(`/${op.entity}s/${op.entityId}`, {
            method: "PUT",
            body: JSON.stringify(op.data),
          });
        } else if (op.type === "soft-delete") {
          await this._http.fetchOrThrow(`/${op.entity}s/${op.entityId}`, {
            method: "DELETE",
          });
        } else if (op.type === "restore") {
          await this._http.fetchOrThrow(`/${op.entity}s/${op.entityId}/restore`, {
            method: "POST",
          });
        } else if (op.type === "delete") {
          const endpoint =
            op.entity === "note"
              ? `/notes/${op.entityId}/permanent`
              : `/${op.entity}s/${op.entityId}`;
          await this._http.fetchOrThrow(endpoint, { method: "DELETE" });
        }
        await this._deleteOpGroup(op, supersededIdsByKey, result);

        if (
          (op.entity === "note" || op.entity === "tag") &&
          (op.type === "update" || op.type === "create")
        ) {
          await this._storage.markEntitySynced(op.entity, op.entityId);
        }

        result.synced++;
      } catch (err) {
        if (isApiError(err) && err.status === 401) return result;
        if (isApiError(err) && err.status === 409) {
          if (op.entity === "note") {
            await this._reportNoteConflict("legacy_409", op, undefined, err);
            await this._storage.markNoteConflict(op.entityId);
          }
          await this._deleteOpGroup(op, supersededIdsByKey, result);
        } else {
          if (op.id) await this._storage.updateSyncOpRetries(op.id, op.retries + 1);
        }
        result.failed++;
      }
    }
    return result;
  }

  // ── pullFromServer (v2 — pull changes since version) ──────────────────

  async pullFromServer(): Promise<{ hasChanges: boolean }> {
    if (!this._isOnline()) return { hasChanges: false };

    const sinceVersion = await this._storage.getServerVersion();

    // `fetchOrThrow`, not `fetch`. The non-throwing variant returns null on
    // failure, and returning `{ hasChanges: false }` from it made a pull that
    // never reached the server indistinguishable from one that found nothing
    // new. `useSync.pull()` reads that as success: it resets
    // `consecutiveFailures` to 0 AND doubles `pullCooldown` (the "no changes,
    // back off" branch), so a client whose every pull 401'd polled less and
    // less often while reporting itself healthy, and nothing reached Sentry.
    // Offline is already short-circuited above and in `useSync.pull()`, so
    // reaching here and throwing means a real failure worth surfacing.
    const pullResponse = await this._http.fetchOrThrow<SyncPullResponse>("/sync/pull", {
      method: "POST",
      body: JSON.stringify({ sinceVersion, limit: 500 }),
    });

    const hasChanges = pullResponse.changes.length > 0;
    // In v2 a pull never writes a note's text, it asks a content pass to
    // catch the moved notes up. Legacy to v2 waits for this pull's changes.
    this._applyCollabFeatures(pullResponse.collabFeatures, "before");
    await this._applySyncChanges(pullResponse);
    // Legacy to v2 once per pull, after its last page: each run may count a
    // deferral, and a page is not a pull.
    if (!pullResponse.hasMore) this._applyCollabFeatures(pullResponse.collabFeatures, "after");
    await this._syncContentSafely();
    return { hasChanges };
  }

  private async _applySyncChanges(response: SyncPullResponse): Promise<void> {
    await this._storage.applyPullChanges(response.changes, response.serverVersion, this._getUserId());

    // If there are more changes, pull again
    if (response.hasMore) {
      // Schedule another pull (don't recurse synchronously)
      setTimeout(() => {
        this.pullFromServer().catch(console.error);
      }, 0);
    }
  }

  // ── fullSync ──────────────────────────────────────────────────────────

  fullSync(): Promise<SyncResult> {
    if (!this._isOnline()) return Promise.resolve({ synced: 0, failed: 0, skipped: 0 });

    if (this._syncInProgress) return this._syncInProgress;

    const run = async (): Promise<SyncResult> => {
      this.broadcastSyncState("syncing");
      try {
        // v2 (PLAN.md task 1.9): metadata push, then content, then pull, then
        // content again for what the pull said moved (both inside push / pull).
        const result = await this.pushToServer();
        await this.pullFromServer();

        try {
          await this._storage.cleanupZombiePendingStates();
        } catch (cleanupErr) {
          console.warn("[Caliu] cleanupZombiePendingStates failed (non-fatal):", cleanupErr);
        }

        return result;
      } catch (err) {
        this.broadcastSyncState("error");
        throw err;
      } finally {
        this._syncInProgress = null;
        if (this._syncState === "syncing") {
          this.broadcastSyncState("idle");
        }
      }
    };

    this._syncInProgress = run();
    return this._syncInProgress;
  }

  // ── Conflict resolution ───────────────────────────────────────────────

  async resolveConflict(
    noteId: string,
    resolution: "local" | "server",
  ): Promise<void> {
    const queued = await this._queuedOpsForNote(noteId);
    // The server has never had this note while its `create` is still queued.
    const neverPushed = queued.some((op) => op.entity === "note" && op.type === "create");

    if (resolution === "server") {
      let serverNote: Note;
      try {
        serverNote = (await this._http.fetchOrThrow<{ note: Note }>(`/notes/${noteId}`)).note;
      } catch (err) {
        // Any failure but a 404 (5xx, network) is transient: leave the
        // conflict in place and rethrow so the user can retry.
        if (!(isApiError(err) && err.status === 404)) throw err;
        // The server has no such note. When the note was never pushed there
        // is no server version to keep, and "keeping" it must not delete the
        // only copy: the conflict stays, and "Keep Mine" is the way out.
        if (neverPushed) return;
        // Otherwise it was deleted (or purged) on another device. Reflect
        // that state locally: drop the note, its ops and its conflict.
        await this._dropQueuedOpsForNote(noteId);
        await this._storage.bulkDeleteNotes([noteId]);
        return;
      }
      const tags = Array.isArray(serverNote.tags) ? normalizeTagIds(serverNote.tags) : [];
      const kept = { ...serverNote, tags, syncStatus: "synced" as const };
      // The user chose the server's version, so every local write still
      // queued for this note goes with the local text. Left in the queue they
      // were pushed on the next sync, and the version just discarded overwrote
      // the one just kept. Ops and note in ONE transaction where the storage
      // offers it; otherwise ops first, so a failure in between leaves the
      // conflict open, never a synced note with a stale op behind it.
      const atomic = (this._storage as Partial<KeepServerStorage>).keepServerNote;
      if (atomic) {
        const opIds = queued.flatMap((op) => (op.id === undefined ? [] : [op.id]));
        await atomic.call(this._storage, kept, opIds);
      } else {
        await this._dropQueuedOpsForNote(noteId);
        await this._storage.putNote(kept);
      }
    } else {
      const note = await this._storage.getNote(noteId);
      if (!note) return;
      // Nothing to overwrite on a server that never had the note: releasing
      // the held ops IS keeping mine. The queued create (with the updates
      // folded into it) pushes on the next sync. No request is made.
      if (neverPushed) {
        await this._storage.updateNoteSyncStatus(noteId, "pending");
        return;
      }
      let res: unknown;
      try {
        res = await this._http.fetchOrThrow(`/notes/${noteId}`, {
          method: "PUT",
          body: JSON.stringify({
            title: note.title,
            content: note.content,
            is_pinned: note.isPinned,
          }),
        });
      } catch (err) {
        // The server no longer has the note: there is no version to overwrite
        // either. Release the held ops instead of leaving a conflict that no
        // choice can close. Anything else leaves the conflict as it is, as
        // before (the user retries).
        if (isApiError(err) && err.status === 404) {
          if (queued.some((op) => op.entity === "note")) {
            await this._storage.updateNoteSyncStatus(noteId, "pending");
          } else {
            // Nothing queued carries the text any more (the op that
            // conflicted was discarded): queue it again. This is the storage's
            // "write this text as pending and enqueue it" (the auto-merge
            // write), with the local text as it is.
            await this._storage.applyAutoMergedNote(noteId, note.content, note.updatedAt);
            // That write keeps a conflict as it finds it (the sticky rule), so
            // the release is said here, as Swift and Kotlin do.
            await this._storage.updateNoteSyncStatus(noteId, "pending");
          }
        }
        return;
      }
      if (res) {
        await this._storage.markNoteSynced(noteId);
        // Writes made while the conflict was open were held in the queue (see
        // pushToServer). The PUT above carried the text and the pin; whatever
        // else they hold (archive, read-only, order, trash, tag links) is
        // released now and pushed by the next sync. The note stays `pending`
        // until then so the PUT's echo cannot be pulled over those fields
        // first.
        // Read again: a write can have been queued while the PUT was out.
        const held = await this._queuedOpsForNote(noteId);
        if (held.some((op) => op.entity === "note")) {
          await this._storage.updateNoteSyncStatus(noteId, "pending");
        }
      }
    }
  }

  private async _dropQueuedOpsForNote(noteId: string): Promise<void> {
    for (const op of await this._queuedOpsForNote(noteId)) {
      if (op.id !== undefined) await this._storage.deleteSyncOp(op.id);
    }
  }

  /**
   * Attempt a git-style three-way merge for a note the server flagged as
   * conflicting. Returns true only when the merge is clean (no overlapping
   * edits) and the merged content was written + re-enqueued for push. Returns
   * false — falling back to manual pick-a-side — when there is no stored merge
   * base, the server snapshot is malformed, or the edits truly overlap.
   */
  private async _tryAutoMergeNoteConflict(
    op: SyncOperation,
    pushResult: SyncPushResult,
    queuedOps: SyncOperation[],
  ): Promise<boolean> {
    try {
      const serverData = pushResult.serverData as Record<string, unknown> | undefined;
      const theirs = serverData?.content;
      const theirsUpdatedAt = serverData?.updatedAt ?? serverData?.updated_at;
      if (typeof theirs !== "string" || typeof theirsUpdatedAt !== "string") return false;

      // The server's text is one this device pushed itself: an earlier push
      // of this note landed and its answer was lost, so the op (and the ops it
      // absorbed) stayed queued and were pushed again with the EARLIEST
      // expected_updated_at, which is older than what that first push wrote.
      // Not a foreign write. The local text descends from the server's, so
      // the server's text is the merge base and the merge is "ours".
      const ownEcho = queuedOps.some(
        (queued) =>
          queued.entity === "note" &&
          queued.entityId === op.entityId &&
          (queued.type === "update" || queued.type === "create") &&
          (queued.data as Record<string, unknown> | undefined)?.content === theirs,
      );
      const base = ownEcho ? theirs : await this._storage.getNoteBaseContent(op.entityId);
      if (base == null) return false; // no ancestor → pick-a-side fallback

      // The merge is computed from the row and written only while the row
      // still holds that text: a save that lands between the read and the
      // write would otherwise be overwritten by a merge that does not contain
      // it. The storage then writes nothing and the merge is redone from the
      // row. Bounded: after three rounds the conflict is the user's.
      const guarded = (this._storage as Partial<GuardedAutoMergeStorage>).applyAutoMergedNoteIfUnchanged;
      for (let round = 0; round < MAX_AUTO_MERGE_ROUNDS; round++) {
        const local = await this._storage.getNote(op.entityId);
        if (!local) return false;

        const result = threeWayMerge(base, local.content, theirs);
        if (!result.clean) return false; // overlapping edits → manual resolution

        if (!guarded) {
          await this._storage.applyAutoMergedNote(op.entityId, result.merged, theirsUpdatedAt);
          return true;
        }
        if (await guarded.call(this._storage, op.entityId, result.merged, theirsUpdatedAt, local.content)) {
          return true;
        }
      }
      return false;
    } catch (err) {
      // Auto-merge is best-effort; on any unexpected failure fall back to the
      // manual conflict path rather than losing the user's edits.
      Sentry.captureException(err);
      return false;
    }
  }

  // ── Helper accessors (delegated to storage) ───────────────────────────

  async getPendingCount(): Promise<number> {
    return this._storage.getPendingCount();
  }

  async getFailedCount(): Promise<number> {
    return this._storage.getFailedCount(MAX_RETRIES);
  }

  async retryFailedOps(): Promise<void> {
    return this._storage.retryFailedOps(MAX_RETRIES);
  }

  async clearFailedOps(): Promise<void> {
    return this._storage.clearFailedOps(MAX_RETRIES);
  }

  async getConflictCount(): Promise<number> {
    return this._storage.getConflictCount();
  }

  // ── Conflict diagnostics ──────────────────────────────────────────────

  /**
   * Capture a rich Sentry log (warning, NOT exception) the moment a note
   * conflict is detected. Designed to debug the "single device should never
   * conflict" symptom — surfaces local note state, the op being pushed (with
   * its `expected_updated_at`), the server's echoed snapshot, and timing.
   *
   * Never logs raw note title/content — only lengths and a non-cryptographic
   * fingerprint so we can correlate without spilling user data.
   */
  private async _reportNoteConflict(
    source: "v2_push" | "legacy_409",
    op: SyncOperation,
    pushResult: SyncPushResult | undefined,
    error?: unknown,
  ): Promise<void> {
    try {
      const attrs: Record<string, string | number | boolean> = {};
      const now = Date.now();
      const opData = (op.data ?? {}) as Record<string, unknown>;
      const serverData = (pushResult?.serverData ?? null) as Record<string, unknown> | null;

      attrs.source = source;
      attrs.note_id = op.entityId;
      attrs.operation = op.type;
      attrs.retries = op.retries;
      attrs.op_queued_at = new Date(op.timestamp).toISOString();
      attrs.op_age_ms = now - op.timestamp;
      attrs.detected_at = new Date(now).toISOString();

      // Pushed payload — pull the most diagnostic fields out and dump keys
      // so we can spot missing expected_updated_at, etc.
      attrs.pushed_keys = Object.keys(opData).join(",");
      const expectedUpdatedAt = pickStr(opData, "expected_updated_at", "expectedUpdatedAt");
      if (expectedUpdatedAt !== undefined) attrs.pushed_expected_updated_at = expectedUpdatedAt;
      const pushedUpdatedAt = pickStr(opData, "updated_at", "updatedAt");
      if (pushedUpdatedAt !== undefined) attrs.pushed_updated_at = pushedUpdatedAt;
      const pushedIsPinned = opData.is_pinned ?? opData.isPinned;
      if (pushedIsPinned !== undefined) attrs.pushed_is_pinned = String(pushedIsPinned);
      const pushedDeletedAt = pickStr(opData, "deleted_at", "deletedAt");
      if (pushedDeletedAt !== undefined) attrs.pushed_deleted_at = pushedDeletedAt;
      if (typeof opData.content === "string") {
        attrs.pushed_content_length = opData.content.length;
        attrs.pushed_content_fp = fingerprint(opData.content);
      }
      if (typeof opData.title === "string") {
        attrs.pushed_title_length = opData.title.length;
      }

      // Local note state at moment of detection. Web storage returns
      // LocalNote (with syncStatus); the shared interface narrows to Note.
      let localUpdatedAt: string | undefined;
      try {
        const note = (await this._storage.getNote(op.entityId)) as
          | (Note & { syncStatus?: string })
          | undefined;
        if (note) {
          if (note.syncStatus) attrs.local_sync_status = note.syncStatus;
          attrs.local_updated_at = note.updatedAt;
          attrs.local_created_at = note.createdAt;
          if (note.deletedAt) attrs.local_deleted_at = note.deletedAt;
          attrs.local_is_pinned = note.isPinned;
          attrs.local_word_count = note.wordCount;
          attrs.local_title_length = note.title?.length ?? 0;
          attrs.local_content_length = note.content?.length ?? 0;
          attrs.local_content_fp = fingerprint(note.content ?? "");
          attrs.local_tags_count = Array.isArray(note.tags) ? note.tags.length : 0;
          localUpdatedAt = note.updatedAt;
        } else {
          attrs.local_note_missing = true;
        }
      } catch {
        // Best-effort — never let diagnostic gathering break the sync flow.
      }

      // Server echo (only present on v2 conflicts).
      let serverUpdatedAt: string | undefined;
      if (serverData) {
        attrs.server_keys = Object.keys(serverData).join(",");
        serverUpdatedAt = pickStr(serverData, "updated_at", "updatedAt");
        if (serverUpdatedAt !== undefined) attrs.server_updated_at = serverUpdatedAt;
        const serverCreatedAt = pickStr(serverData, "created_at", "createdAt");
        if (serverCreatedAt !== undefined) attrs.server_created_at = serverCreatedAt;
        const serverDeletedAt = pickStr(serverData, "deleted_at", "deletedAt");
        if (serverDeletedAt !== undefined) attrs.server_deleted_at = serverDeletedAt;
        const serverIsPinned = serverData.is_pinned ?? serverData.isPinned;
        if (serverIsPinned !== undefined) attrs.server_is_pinned = String(serverIsPinned);
        if (typeof serverData.content === "string") {
          attrs.server_content_length = serverData.content.length;
          attrs.server_content_fp = fingerprint(serverData.content);
        }
        if (typeof serverData.title === "string") {
          attrs.server_title_length = serverData.title.length;
        }
      }

      if (pushResult?.version != null) attrs.push_result_version = pushResult.version;
      if (pushResult?.error) attrs.push_result_error = pushResult.error;
      if (error instanceof Error) attrs.legacy_error = error.message;

      try {
        attrs.server_version_watermark = await this._storage.getServerVersion();
      } catch {
        /* best-effort */
      }
      try {
        attrs.pending_ops_count = await this._storage.getPendingCount();
      } catch {
        /* best-effort */
      }

      // Pre-computed deltas — single most useful signal for "why did this
      // conflict on a single device?". expected_vs_server_ms === 0 with a
      // conflict status is pathological and points straight at the bug.
      const expectedDelta = timeDeltaMs(expectedUpdatedAt, serverUpdatedAt);
      if (expectedDelta != null) attrs.expected_vs_server_ms = expectedDelta;
      const localDelta = timeDeltaMs(localUpdatedAt, serverUpdatedAt);
      if (localDelta != null) attrs.local_vs_server_ms = localDelta;
      if (expectedUpdatedAt != null && localUpdatedAt != null) {
        attrs.expected_matches_local = expectedUpdatedAt === localUpdatedAt;
      }

      addDeviceAttributes(attrs);
      logSyncConflict(attrs);
    } catch {
      // Diagnostics must never throw and break sync.
    }
  }
}

// ── Helpers ────────────────────────────────────────────────────────────

/** Quick non-cryptographic fingerprint (DJB2). Used to correlate identical
 * content across local/server snapshots without leaking the bytes. */
function fingerprint(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = (h * 33 + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(16);
}

function timeDeltaMs(a: string | null | undefined, b: string | null | undefined): number | null {
  if (!a || !b) return null;
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  if (Number.isNaN(ta) || Number.isNaN(tb)) return null;
  return ta - tb;
}

function pickStr(obj: Record<string, unknown>, ...keys: string[]): string | undefined {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "string") return value;
  }
  return undefined;
}

function addDeviceAttributes(attrs: Record<string, string | number | boolean>): void {
  try {
    if (typeof localStorage !== "undefined") {
      const id = localStorage.getItem("caliu_device_id");
      if (id) attrs.device_id = id;
    }
  } catch {
    /* localStorage may throw in private mode */
  }
  if (typeof navigator !== "undefined") {
    attrs.online = navigator.onLine;
    attrs.user_agent = navigator.userAgent;
  }
}

